import React from "react";
import { Label } from "components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { SearchIcon } from "lucide-react";
import { Checkbox, Input } from "@chakra-ui/react";
import { cn } from "@/lib/utils";
import { formatCnpj } from "@/utils/cnpj-formatter";
import { digits, norm } from "@/utils/strings";

export type StoreItem = { code: string; cnpj?: string };

type Props = {
  id?: string;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptySearchText?: string;
  className?: string;
  items?: StoreItem[];
  selectedCnpjs: string[];
  onChange: (cnpjs: string[]) => void;
};

export default function InputMultiSelectStore({
  id, label = "Loja", placeholder = "Selecionar loja",
  searchPlaceholder = "Pesquisar...", emptySearchText = "Nenhum resultado encontrado",
  className, items = [], selectedCnpjs, onChange,
}: Props) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const searchRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!isOpen) setQuery("");
    const t = setTimeout(() => { if (isOpen) searchRef.current?.focus(); }, 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  const filtered = React.useMemo(() => {
    const q = query.trim();
    if (!q) return items;

    const tokens = q.split(/\s+/).filter(Boolean);
    const qDigits = digits(q);

    return items.filter((it) => {
      const code = it.code ?? "";
      const codeNorm = norm(code);
      const cnpjDigits = digits(it.cnpj);

      const matchesAllTokens = tokens.every((t) => {
        const td = digits(t);
        if (td) return code.includes(td) || cnpjDigits.includes(td);
        return codeNorm.includes(norm(t));
      });

      const quickDigits =
        qDigits.length > 0 && (code.includes(qDigits) || cnpjDigits.includes(qDigits));

      return matchesAllTokens || quickDigits;
    });
  }, [items, query]);

  const allVisibleChecked = filtered.length > 0 && filtered.every(it => selectedCnpjs.includes(it.code));
  const someVisibleChecked = filtered.some(it => selectedCnpjs.includes(it.code)) && !allVisibleChecked;

  const toggleOne = (code: string) => {
    const set = new Set(selectedCnpjs);
    set.has(code) ? set.delete(code) : set.add(code);
    onChange([...set]);
  };
  const toggleAllVisible = () => {
    const set = new Set(selectedCnpjs);
    allVisibleChecked ? filtered.forEach(it => set.delete(it.code)) : filtered.forEach(it => set.add(it.code));
    onChange([...set]);
  };
  const clearAll = () => onChange([]);

  const summary = React.useMemo(() => {
    if (selectedCnpjs.length === 0) return placeholder;
    if (selectedCnpjs.length === 1) return `Loja ${selectedCnpjs[0]}`;
    return `${selectedCnpjs.length} lojas selecionadas`;
  }, [selectedCnpjs, placeholder]);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</Label>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            ref={buttonRef} id={id} type="button"
            className={cn(
              "flex items-center w-full h-8 px-3 border rounded-md text-left text-sm text-gray-700 bg-white",
              "hover:border-[#F5CB2B] hover:bg-[#F2F2F2]"
            )}
            onClick={() => setIsOpen(o => !o)}
          >
            <span className="flex-1 truncate">{summary}</span>
            <ChevronDownIcon className="text-gray-500 w-5 h-5 ml-1" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="p-0" align="start"
          style={{ minWidth: buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "auto" }}
        >
          {/* Busca full-width */}
          <div className="sticky top-0 z-10 bg-white border-b">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-9 pr-3 h-10 w-full rounded-none border-0 focus-visible:ring-0"
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Cabeçalho centralizado */}
          <div className="px-0 py-2 bg-gray-50 border-b text-xs text-gray-600">
            <div className="grid grid-cols-[36px_100px_1fr] items-center">
              <div />{/* checkbox col */}
              <div className="text-center font-medium">Loja</div>
              <div className="text-center font-medium">CNPJ</div>
            </div>
          </div>

          {/* Lista */}
          <div className="max-h-[360px] overflow-y-auto">
            {filtered.length > 0 && (
              <button
                type="button"
                className="w-full text-center text-xs text-gray-600 hover:bg-gray-50 px-3 py-2 border-b"
                onClick={toggleAllVisible}
              >
                {allVisibleChecked ? "Desmarcar todos da busca" : "Selecionar todos da busca"}
                {someVisibleChecked && " (parcialmente selecionado)"}
              </button>
            )}

            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500 text-center">{emptySearchText}</div>
            ) : (
              filtered.map((it) => {
                const checked = selectedCnpjs.includes(it.code);
                return (
                  <button
                    key={it.code} type="button"
                    className={cn(
                      "w-full px-3 py-2 hover:bg-gray-50",
                      "grid grid-cols-[36px_100px_1fr] items-center"
                    )}
                    onClick={() => toggleOne(it.code)}
                  >
                    <div className="flex items-center justify-center">
                      <Checkbox
                        isChecked={checked}                    // Chakra => isChecked
                        onChange={() => toggleOne(it.code)}
                        onClick={(e) => e.stopPropagation()}   // evita toggle duplo
                        size="sm"
                      />
                    </div>
                    <div className="text-sm text-gray-800 text-center tabular-nums">
                      {it.code}
                    </div>
                    <div className="text-sm text-gray-700 text-center">
                      {formatCnpj(it.cnpj ?? "")}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-3 py-2 border-t bg-white">
            <span className="text-xs text-gray-600">
              {selectedCnpjs.length} selecionada{selectedCnpjs.length === 1 ? "" : "s"}
            </span>
            <div className="flex gap-2">
              <button type="button" className="text-xs px-2 py-1 rounded border hover:bg-gray-50" onClick={clearAll}>Limpar</button>
              <button type="button" className="text-xs px-2 py-1 rounded border bg-gray-100 hover:bg-gray-200" onClick={() => setIsOpen(false)}>Fechar</button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
